import {priority, wcfclient} from '@priority-software/priority-proto-api/lib';
import {getAdditionalProps} from '../proto/core/loginProps';
import {callServer, callServerBatch, Options} from '../proto/core/server';
import {ServerMethods} from '../proto/core/constants';
import {common} from '@priority-software/priority-proto-api/lib/nform';
import {IFilter} from '../../stores/Form/Filter';
import {getTabulaIni} from '../../constants/metaData';
import {getKeyByLang} from '../../features/Form/utils/custom-properties';
import {formsStore} from '../../stores/Form/FormsStore';
import {CachedFields} from '../../stores/Form/FormsCache';
import {PAGE_LIMIT} from '../../features/Form/consts/pagination';
import {ISort} from '../../stores/Form/Sort';

export const noDefaultMessageHandling = {
    handleMessages: false // our messages should be handled outside `callServer` function
};

export const startForm = (
    formName: string,
    options?: Partial<Options>,
    hiddenFields?: string[]
): Promise<priority.netitems.form.StartRs> => {
    return callServer(
        wcfclient.start,
        new priority.netitems.form.StartRq({
            Fname: formName,
            Properties: getAdditionalProps(),
            hiddenFields
        }),
        ServerMethods.StartRq,
        options
    );
};

export const startSubForm = (
    Session: string,
    Fname: string,
    Keys: priority.netitems.form.IField[],
    options?: Partial<Options>
) => {
    return callServer(
        wcfclient.subform,
        new priority.netitems.form.SubformRq({
            Fname,
            Keys,
            Session,
            Properties: getAdditionalProps()
        }),
        ServerMethods.SubformRq,
        options
    );
};

export const recordDelete = (
    Session: string,
    Keys: priority.netitems.form.IField[],
    options: Partial<Options> = noDefaultMessageHandling
) => {
    return callServer(
        wcfclient.recordDelete,
        new priority.netitems.form.RecordDeleteRq({
            Keys,
            Session,
            Properties: getAdditionalProps()
        }),
        ServerMethods.RecordDeleteRq,
        options
    );
};

export const endForm = (Session: string): Promise<priority.netitems.form.EndRs> => {
    return callServer(
        wcfclient.end,
        new priority.netitems.form.EndRq({
            Session,
            Properties: getAdditionalProps()
        }),
        ServerMethods.EndRq
    );
};

export const fetchColumns = (
    session: string,
    options?: Partial<Options>
): Promise<priority.netitems.form.ColumnsRs> => {
    return callServer(
        wcfclient.columns,
        new priority.netitems.form.ColumnsRq({
            Session: session,
            Properties: getAdditionalProps()
        }),
        ServerMethods.ColumnsRq,
        options
    );
};

export const fetchHTML = async (session: string, options?: Partial<Options>) => {
    return callServer(
        wcfclient.htmlFetch,
        new priority.netitems.form.HtmlFetchRq({
            Session: session,
            Properties: getAdditionalProps()
        }),
        ServerMethods.HtmlFetchRq,
        options
    );
};

export type RecordEditParams = {
    Keys: priority.netitems.form.IField[];
    Attachments?: priority.netitems.form.AttachmentOptions;
    ScrLine?: number;
};

export const recordEdit = async (
    session: string,
    params: RecordEditParams,
    options: Partial<Options> = noDefaultMessageHandling
) => {
    return callServer(
        wcfclient.recordEdit,

        new priority.netitems.form.RecordEditRq({
            Session: session,
            Properties: getAdditionalProps(),
            Keys: params.Keys,
            Attachments: params.Attachments,
            ScrLine: params.ScrLine
        }),
        ServerMethods.RecordEditRq,
        options
    );
};

export type RecordUndoParams = {
    Attachments: priority.netitems.form.AttachmentOptions;
};

export const recordUndo = async (
    session: string,
    params: RecordUndoParams,
    options?: Partial<Options>
) => {
    return callServer(
        wcfclient.recordUndo,

        new priority.netitems.form.RecordUndoRq({
            Session: session,
            Properties: getAdditionalProps(),
            Attachments: params.Attachments
        }),
        ServerMethods.RecordUndoRq,
        options
    );
};

export const fieldUpdate = async (
    session: string,
    Field: priority.netitems.form.IField | null,
    options: Partial<Options> = noDefaultMessageHandling
) => {
    return callServer(
        wcfclient.fieldUpdate,
        new priority.netitems.form.FieldUpdateRq({
            Field,
            Session: session,
            Properties: getAdditionalProps()
        }),
        ServerMethods.FieldUpdateRq,
        options
    );
};

export const recordSave = async (
    session: string,
    options: Partial<Options> = noDefaultMessageHandling
) => {
    return callServer(
        wcfclient.recordSave,
        new priority.netitems.form.RecordSaveRq({
            Session: session,
            Properties: getAdditionalProps()
        }),
        ServerMethods.RecordSaveRq,
        options
    );
};

export type FetchFormDataParams = {
    session: string;
    filters?: Array<IFilter> | priority.netitems.form.IFilter[];
    sort?: Array<ISort>;
    top?: number;
    options?: Partial<Options>;
    attachments?: boolean;
    filtersOperator?: priority.netitems.shared.LogicalOperator;
    skip?: number;
    select?: number[];
};

export const fetchFormData = (
    params: FetchFormDataParams
): Promise<priority.netitems.form.FetchRs> => {
    const {
        session,
        filters = [],
        sort = [],
        top = PAGE_LIMIT,
        filtersOperator = priority.netitems.shared.LogicalOperator.Or,
        skip,
        attachments,
        options,
        select
    } = params;
    return callServer(
        wcfclient.recordsFetch,
        new priority.netitems.form.FetchRq({
            Session: session,
            Properties: getAdditionalProps(),
            Filters: filters,
            Sort: sort,
            FiltersOp: filtersOperator,
            Top: top,
            Skip: skip,
            Select: select,
            Attachments: attachments
                ? priority.netitems.form.AttachmentOptions.Attach_Data
                : priority.netitems.form.AttachmentOptions.Attach_NoData
        }),
        ServerMethods.FetchRq,
        options
    );
};

export const fetchSubForms = (
    session: string,
    options?: Partial<Options>
): Promise<priority.netitems.form.SubformsRs> => {
    return callServer(
        wcfclient.subforms,
        new priority.netitems.form.SubformsRq({
            Session: session,
            Properties: getAdditionalProps()
        }),
        ServerMethods.SubformsRq,
        options
    );
};

export const getFormCustomData = async (
    Keys: Array<string>,
    CurrentScopeCompany: boolean,
    CurrentScopeUser?: boolean
) => {
    try {
        const params = new priority.netitems.ngtw.DataStoreGetRq({
            Keys,
            CurrentScopeUser,
            CurrentScopeCompany,
            Properties: getAdditionalProps()
        });

        const res = await callServer(wcfclient.dataStoreGet, params, ServerMethods.DataStoreGetRq);

        return res?.StoreMap || {};
    } catch (e) {
        throw new Error((e as Error).message);
    }
};

export const setFormCustomData = async (
    StoreMap?: {[k: string]: common.IConstant} | null,
    formName?: string,
    CurrentScopeCompany?: boolean,
    CurrentScopeUser?: boolean
) => {
    try {
        const data = Object.entries(StoreMap);
        const newData = {};

        data.forEach(item => {
            const [fieldName, value] = item;
            const key = getKeyByLang(formName, 'CUSTOM_DATA', fieldName);
            newData[key] = value;
        });

        const params = new priority.netitems.ngtw.DataStoreSetRq({
            Properties: getAdditionalProps(),
            StoreMap: newData,
            CurrentScopeUser,
            CurrentScopeCompany
        });

        // Update client side cache data
        const {cachedData} = formsStore;
        const storedMetaData = cachedData.getData(formName, CachedFields.formMetaData) || {};
        cachedData.setData(formName, CachedFields.formMetaData, {...storedMetaData, ...newData});

        await callServer(wcfclient.dataStoreSet, params, ServerMethods.DataStoreSetRq);
    } catch (e) {
        throw new Error((e as Error).message);
    }
};

export const fetchHelp = (
    Fname: string,
    ColName?: string,
    options?: Partial<Options>
): Promise<priority.netitems.form.HelpRs> => {
    return callServer(
        wcfclient.help,
        new priority.netitems.form.HelpRq({
            Fname,
            ColName,
            Properties: getAdditionalProps()
        }),
        ServerMethods.HelpRq,
        options
    );
};

export const fetchFormActions = (Session: string, options?: Partial<Options>) => {
    return callServer(
        wcfclient.actions,
        new priority.netitems.form.ActionsRq({
            Session,
            Properties: getAdditionalProps()
        }),
        ServerMethods.ActionsRq,
        options
    );
};

export const formSystemConstants = (): Promise<priority.netitems.form.SystemConstantsRs> => {
    return callServer(
        wcfclient.formSystemConstants,
        new priority.netitems.form.SystemConstantsRq({
            Properties: getAdditionalProps()
        }),
        ServerMethods.SystemConstantsRq
    );
};

export const fieldSelect = async (Params: Partial<priority.netitems.form.IFieldSelectRq> = {}) => {
    const params = {
        Properties: getAdditionalProps(),
        Page: 1,
        ...Params
    };

    return callServer(
        wcfclient.fieldSelect,
        new priority.netitems.form.FieldSelectRq(params),
        ServerMethods.FieldSelectRq,
        {
            keepFocus: true
        }
    );
};

export const recordZoom = (
    Session: string,
    Keys: priority.netitems.form.IField[]
): Promise<priority.netitems.form.RecordZoomRs> => {
    return callServer(
        wcfclient.recordZoom,
        new priority.netitems.form.RecordZoomRq({
            Properties: getAdditionalProps(),
            Session,
            Keys
        }),
        ServerMethods.RecordZoomRq
    );
};

export const action = async (
    Session: string,
    ScrLine: number | Long,
    Keys: priority.netitems.form.IField[]
) => {
    return callServer(
        wcfclient.action,
        new priority.netitems.form.ActionRq({
            Session,
            Keys,
            ScrLine,
            Properties: getAdditionalProps()
        }),
        ServerMethods.ActionRq
    );
};

export const actionEnd = async (Session: string) => {
    return callServer(
        wcfclient.actionEnd,
        new priority.netitems.form.ActionEndRq({
            Session,
            Properties: getAdditionalProps()
        }),
        ServerMethods.ActionEndRq
    );
};

export const getStatuses = (Fname: string) => {
    return callServer(
        wcfclient.statuses,
        new priority.netitems.form.StatusesRq({
            Properties: getAdditionalProps(),
            Fname
        }),
        ServerMethods.StatusesRq
    );
};

export const saveHTML = async (Session: string, htmlContent: string) => {
    return callServer(
        wcfclient.htmlSave,
        new priority.netitems.form.HtmlSaveRq({
            Session,
            Properties: getAdditionalProps(),
            Content: htmlContent
        }),
        ServerMethods.HtmlSaveRq
    );
};

export const htmlSignature = async (): Promise<priority.netitems.form.HtmlSignatureRs> => {
    return await callServer(
        wcfclient.htmlSignature,
        new priority.netitems.form.HtmlSignatureRq({
            Properties: getAdditionalProps()
        }),
        ServerMethods.HtmlSignatureRq
    );
};

export const unlockHtmlForm = (Session: string) => {
    return callServer(
        wcfclient.htmlUnlock,
        new priority.netitems.form.HtmlUnlockRq({
            Properties: getAdditionalProps(),
            Session
        }),
        ServerMethods.HtmlUnlockRq
    );
};

export const batch = async (
    tasks: priority.netitems.form.Grpc2Net[],
    session: string,
    options?: Partial<Options>
): Promise<priority.netitems.form.BatchRs> => {
    return callServerBatch(
        wcfclient.batch,
        new priority.netitems.form.BatchRq({
            Tabulaini: getTabulaIni(),
            Tasks: tasks
        }),
        session,
        ServerMethods.BatchRq,
        options
    );
};

export const fetchCount = async (
    session: string,
    filters: priority.netitems.form.IFilter[],
    filtersOp?: priority.netitems.shared.LogicalOperator,
    options?: Partial<Options>
): Promise<priority.netitems.form.CountFetchRs> => {
    return callServer(
        wcfclient.CountFetch,
        new priority.netitems.form.CountFetchRq({
            Properties: getAdditionalProps(),
            Session: session,
            Filters: filters,
            FiltersOp: filtersOp || priority.netitems.shared.LogicalOperator.And
        }),
        ServerMethods.CountFetchRq,
        options
    );
};
